export type CardType = 'Visa Debit' | 'Visa Credit' | 'Mastercard Debit' | 'Mastercard Credit' | 'Amex Debit' | 'Amex Credit' | 'Gold' | 'Platinum' | 'Other';

export type ExpenseCategory = 'Loan' | 'Salary' | 'Personal' | 'Bank Fee' | 'Other';

export interface Business {
  id: string;
  user_id: string;
  name: string;
  stripe_key_encrypted: string;
  wise_api_key_encrypted: string | null;
  mercury_api_key_encrypted: string | null;
  bank_rate: number;
  credit_card_rate: number;
  default_black_market_rate: number;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: string;
  business_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  stripe_customer_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Card {
  id: string;
  customer_id: string;
  card_type: CardType;
  limit_usd: number;
  remaining_balance_usd: number;
  last_reset_date: string;
  created_at: string;
  updated_at: string;
}

export interface Payment {
  id: string;
  business_id: string;
  customer_id: string;
  card_id: string | null;
  amount_usd: number;
  stripe_charge_id: string | null;
  payment_date: string;
  created_at: string;
  updated_at: string;
}

export interface Expense {
  id: string;
  business_id: string;
  amount_mvr: number;
  amount_usd: number | null;
  expense_date: string;
  category: ExpenseCategory;
  note: string;
  fee_source: string | null;
  fee_reference: string | null;
  created_at: string;
  updated_at: string;
}

export interface DailyRate {
  id: string;
  business_id: string;
  rate_date: string;
  black_market_rate: number;
  created_at: string;
}

export interface CustomerWithCards extends Customer {
  cards: Card[];
}

export interface PaymentWithDetails extends Payment {
  customer: Customer;
  card: Card | null;
}

export interface UserPreferences {
  id: string;
  user_id: string;
  email_new_payments: boolean;
  email_rate_changes: boolean;
  email_low_profit: boolean;
  dark_mode: boolean;
  auto_sync_interval: number;
  created_at: string;
  updated_at: string;
}

export type XmCardBrand =
  | 'Visa Debit'
  | 'Visa Credit Classic'
  | 'Visa Credit Gold'
  | 'Master Debit'
  | 'Master Credit Classic'
  | 'Master Credit Gold'
  | 'CBM Credit';

export interface XmCustomer {
  id: string;
  business_id: string;
  name: string;
  month_year: string;
  created_at: string;
}

export interface XmCustomerPayment {
  id: string;
  customer_id: string;
  business_id: string;
  month_year: string;
  card_brand: XmCardBrand;
  charged_amount: number;
  full_limit: number;
  payment_date: string;
  created_at: string;
}

export interface XmCustomerWithPayments extends XmCustomer {
  payments: XmCustomerPayment[];
}

export const XM_CARD_BRANDS: XmCardBrand[] = [
  'Visa Debit',
  'Visa Credit Classic',
  'Visa Credit Gold',
  'Master Debit',
  'Master Credit Classic',
  'Master Credit Gold',
  'CBM Credit',
];

export type CardLimitGroup = 'debit' | 'credit_classic' | 'visa_credit_gold' | 'master_credit_gold' | 'cbm_credit';

export const CARD_LIMIT_GROUP: Record<XmCardBrand, CardLimitGroup> = {
  'Visa Debit': 'debit',
  'Master Debit': 'debit',
  'Visa Credit Classic': 'credit_classic',
  'Master Credit Classic': 'credit_classic',
  'Visa Credit Gold': 'visa_credit_gold',
  'Master Credit Gold': 'master_credit_gold',
  'CBM Credit': 'cbm_credit',
};

export const CARD_GROUP_LIMITS: Record<CardLimitGroup, number> = {
  debit: 1000,
  credit_classic: 1400,
  visa_credit_gold: 1500,
  master_credit_gold: 1500,
  cbm_credit: 750,
};

export const CARD_GROUP_LABELS: Record<CardLimitGroup, string> = {
  debit: 'Visa/Master Debit',
  credit_classic: 'Visa/Master Credit Classic',
  visa_credit_gold: 'Visa Credit Gold',
  master_credit_gold: 'Master Credit Gold',
  cbm_credit: 'CBM Credit',
};

export const CARD_TYPE_DEFAULTS: Record<CardType, number> = {
  'Visa Debit': 750,
  'Visa Credit': 1500,
  'Mastercard Debit': 750,
  'Mastercard Credit': 1500,
  'Amex Debit': 750,
  'Amex Credit': 1500,
  'Gold': 0,
  'Platinum': 0,
  'Other': 0,
};

export interface OwnedCard {
  type: XmCardBrand;
  pool: CardLimitGroup;
  limit_usd: number;
  card_number?: string;
  expiry_date?: string;
  cvv?: string;
}

export interface GlobalCustomer {
  id: string;
  name: string;
  email: string | null;
  owned_cards: OwnedCard[];
  created_at: string;
  updated_at: string;
}

export const CARD_POOL_MAP: Record<XmCardBrand, CardLimitGroup> = {
  'Visa Debit': 'debit',
  'Master Debit': 'debit',
  'Visa Credit Classic': 'credit_classic',
  'Master Credit Classic': 'credit_classic',
  'Visa Credit Gold': 'visa_credit_gold',
  'Master Credit Gold': 'master_credit_gold',
  'CBM Credit': 'cbm_credit',
};

export const CARD_POOL_LIMITS: Record<CardLimitGroup, number> = {
  debit: 1000,
  credit_classic: 1400,
  visa_credit_gold: 1500,
  master_credit_gold: 1500,
  cbm_credit: 750,
};

export interface MoneyFlowEvent {
  id: string;
  business_id: string;
  business_name: string;
  event_date: string;
  wise_usd_sent: number;
  fee_usd: number;
  exchange_rate_lkr_usd: number;
  p2p_rate_lkr_usdt: number;
  usdt_sent: number;
  mvr_rate_per_usdt: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}
